import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useUserRole } from "../../hooks/useUserRole";

import {
  useAddAssociatedCampaignMutation,
  useDuplicateAssociatedCampaignMutation,
  useGetPlatformMappingsQuery,
  PlatformMapping,
  AssociatedCampaignWithRelations,
  useGetPlatformCandidatesMutation,
} from "../../store/apis/briefingApi";
import { useGetCompanyAssignmentsQuery } from "../../store/apis/companyApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components, operations } from "../../types/api";
import { CandidateSelector } from "./CandidateSelector";
import { defaultInvestmentPayment } from "../../utils/companyAssignments";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type CreateAssociatedCampaignRequest =
  operations["AddAssociatedCampaign"]["requestBody"]["content"]["application/json"];
type BriefingFunnel = components["schemas"]["_36_Enums.BriefingFunnel"];

interface AddAssociatedCampaignDialogProps {
  open: boolean;
  briefingId: string;
  briefing?: components["schemas"]["BriefingDetailResponse"];
  platformOptions: BriefingPlatform[];
  onClose: () => void;
  onSuccess?: () => void;
  duplicateMode?: boolean;
  campaignToDuplicate?: AssociatedCampaignWithRelations;
}

export const AddAssociatedCampaignDialog: React.FC<
  AddAssociatedCampaignDialogProps
> = ({
  open,
  onClose,
  briefingId,
  briefing,
  platformOptions,
  onSuccess,
  duplicateMode,
  campaignToDuplicate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { role, isClient } = useUserRole();
  const isAccount = role === "ACCOUNT";
  const canSetInvestment =
    role === "ADMIN" || role === "ACCOUNT" || role === "PRICING";

  const [name, setName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [campaignLevel, setCampaignLevel] =
    useState<components["schemas"]["EntityType"]>("CAMPAIGN");
  const [advertiserId, setAdvertiserId] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<
    BriefingPlatform | ""
  >("");
  const [selectedCampaignType, setSelectedCampaignType] = useState<
    components["schemas"]["BriefingCampaignType"] | ""
  >("");
  const [funnel, setFunnel] = useState<BriefingFunnel | "">("");
  const [nativeAdProvider, setNativeAdProvider] = useState<
    "TABOOLA" | "OUTBRAIN" | null
  >(null);
  const [investmentPayment, setInvestmentPayment] = useState<
    "CLIENTE" | "TI_MEDIA_GROUP_SL" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [subcampaignName, setSubcampaignName] = useState("");
  const [visibleToClients, setVisibleToClients] = useState(true);
  // Si quien crea la línea ha tocado el pago de inversión: entonces ya no se le pone el de
  // la empresa encima.
  const paymentEdited = useRef(false);
  // Track whether the user has interacted with the form after dialog open in duplicate mode.
  // Until they do, the auto-fill effect is suppressed so the pre-filled "(Copia)" name is preserved.
  const userHasEdited = useRef(false);

  const { data: platformMappingsData, isLoading: isLoadingMappings } =
    useGetPlatformMappingsQuery();
  const platformMappings: PlatformMapping[] = platformMappingsData?.data ?? [];

  const [addAssociatedCampaign, { isLoading: isAdding }] =
    useAddAssociatedCampaignMutation();
  const [duplicateAssociatedCampaign, { isLoading: isDuplicating }] =
    useDuplicateAssociatedCampaignMutation();

  const [
    getPlatformCandidates,
    {
      data: platformCandidatesData,
      isLoading: isLoadingCandidates,
      error: candidatesError,
    },
  ] = useGetPlatformCandidatesMutation();

  const platformEntry = platformMappings.find(
    (m) => m.platform === selectedPlatform
  );
  const defaultCampaignLevel =
    platformEntry?.defaultCampaignLevel ?? "CAMPAIGN";
  const canFetchCampaigns = !!platformEntry && !!briefing?.companyId;

  // Quién paga por defecto la empresa en la plataforma operativa de la línea. Solo para
  // quien ve el campo; si no, lo pone el backend al crearla.
  const { data: companyAssignmentsData } = useGetCompanyAssignmentsQuery(
    briefing?.companyId ?? "",
    {
      skip: !open || !canSetInvestment || duplicateMode || !briefing?.companyId,
    }
  );
  const companyPayment = defaultInvestmentPayment(
    companyAssignmentsData?.data ?? undefined,
    platformEntry?.operatingPlatform
  );

  // Reset the chosen level/advertiser whenever the platform's default level changes.
  // The CandidateSelector overrides these when the user picks a campaign or ad set.
  useEffect(() => {
    setCampaignLevel(defaultCampaignLevel);
    setAdvertiserId("");
  }, [defaultCampaignLevel]);

  useEffect(() => {
    if (
      selectedPlatform &&
      briefingId &&
      briefing?.companyId &&
      !duplicateMode
    ) {
      getPlatformCandidates({
        id: briefingId,
        body: { platformType: selectedPlatform as BriefingPlatform },
      });
    }
  }, [
    selectedPlatform,
    briefingId,
    briefing?.companyId,
    duplicateMode,
    getPlatformCandidates,
  ]);

  const isSaving = isAdding || isDuplicating;

  /** Campaign types available for the currently selected platform */
  const availableCampaignTypes = selectedPlatform
    ? (platformMappings.find((m) => m.platform === selectedPlatform)
        ?.campaignTypes ?? [])
    : [];

  /** Format auto-derived from platform mapping + selected campaign type */
  const derivedFormat: components["schemas"]["CampaignFormats"] = (() => {
    if (!selectedPlatform) return "ALL";
    const mapping = platformMappings.find(
      (m) => m.platform === selectedPlatform
    );
    if (!mapping) return "ALL";
    if (selectedCampaignType && mapping.formatByType[selectedCampaignType]) {
      return mapping.formatByType[selectedCampaignType]!;
    }
    return mapping.defaultFormat;
  })();

  // Reset on open/close
  useEffect(() => {
    if (open) {
      if (duplicateMode && campaignToDuplicate) {
        // Reset interaction flag — auto-fill is suppressed until the user edits a field
        userHasEdited.current = false;
        setSourceId("");
        setSubcampaignName(campaignToDuplicate.subcampaignName ?? "");
        setSelectedPlatform(campaignToDuplicate.platform);
        setSelectedCampaignType(campaignToDuplicate.briefingCampaignType ?? "");

        const parts = campaignToDuplicate.name.split("_");
        const lastPart = parts[parts.length - 1];
        setFunnel(
          ["AWARENESS", "CONSIDERATION", "PERFORMANCE"].includes(lastPart)
            ? (lastPart as BriefingFunnel)
            : ""
        );

        setNativeAdProvider(campaignToDuplicate.nativeAdProvider ?? null);
        setInvestmentPayment(campaignToDuplicate.investmentPayment ?? null);
        setVisibleToClients(campaignToDuplicate.visibleToClients ?? true);
        setError(null);

        // Always pre-fill name with "(Copia)" suffix in duplicate mode.
        // The auto-fill effect will only override this once the user interacts.
        setName(`${campaignToDuplicate.name} (Copia)`);
      }
    } else {
      userHasEdited.current = false;
      setName("");
      setSourceId("");
      setCampaignLevel("CAMPAIGN");
      setAdvertiserId("");
      setSubcampaignName("");
      setSelectedPlatform("");
      setSelectedCampaignType("");
      setFunnel("");
      setNativeAdProvider(null);
      setInvestmentPayment(null);
      setVisibleToClients(true);
      setError(null);
    }
  }, [open, duplicateMode, campaignToDuplicate]);

  // Reset campaign type and ad provider when platform changes
  useEffect(() => {
    setSelectedCampaignType("");
    if (selectedPlatform !== "NATADS") {
      setNativeAdProvider(null);
    }
    paymentEdited.current = false;
  }, [selectedPlatform]);

  // El pago de inversión arranca con el de la empresa para la plataforma elegida, hasta
  // que se toque a mano. Va después del reset de arriba para que vea paymentEdited a false.
  useEffect(() => {
    if (open && !duplicateMode && !paymentEdited.current) {
      setInvestmentPayment(companyPayment);
    }
  }, [open, duplicateMode, selectedPlatform, companyPayment]);

  // Auto-fill Campaign Name based on briefing, platform, type, funnel.
  // In duplicate mode, this is suppressed until the user has edited a field
  // so the pre-filled "(Copia)" name is preserved as the initial value.
  useEffect(() => {
    if (open && briefing && selectedPlatform) {
      // In duplicate mode, skip auto-fill until the user makes a change
      if (duplicateMode && !userHasEdited.current) {
        return;
      }
      const parts = [
        briefing.brandOrProduct,
        subcampaignName.trim() || undefined,
        briefing.campaignName,
        selectedPlatform,
        selectedCampaignType || undefined,
        funnel || undefined,
        duplicateMode ? "COPIA" : undefined,
      ].filter(Boolean);
      setName(parts.join("_"));
    }
  }, [
    selectedPlatform,
    selectedCampaignType,
    funnel,
    briefing,
    subcampaignName,
    open,
    duplicateMode,
    campaignToDuplicate,
  ]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t("briefingsView.manualNameRequired", "Name is required."));
      return;
    }
    if (!selectedPlatform) {
      setError(t("briefingsView.selectPlatformFirst", "Select a platform."));
      return;
    }
    if (selectedPlatform === "NATADS" && !isAccount && !nativeAdProvider) {
      setError(
        t(
          "briefingsView.selectNativeProvider",
          "Select a Native Ad Provider (TABOOLA or OUTBRAIN)."
        )
      );
      return;
    }

    setError(null);

    try {
      if (duplicateMode && campaignToDuplicate) {
        await duplicateAssociatedCampaign({
          briefingId,
          associatedId: campaignToDuplicate.id,
          name: name.trim(),
          subcampaignName: subcampaignName.trim() || undefined,
        }).unwrap();

        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.duplicateAssociatedSuccess",
              "Associated campaign duplicated successfully"
            ),
            severity: "success",
          })
        );
      } else {
        const payload: CreateAssociatedCampaignRequest = {
          name: name.trim(),
          sourceId: sourceId.trim() || undefined,
          subcampaignName: subcampaignName.trim() || undefined,
          platform: selectedPlatform,
          nativeAdProvider: nativeAdProvider ?? undefined,
          // Vacío sin tocar = que decida el backend con el de la empresa; vaciado a mano =
          // sin definir.
          investmentPayment: paymentEdited.current
            ? investmentPayment
            : (investmentPayment ?? undefined),
          campaignLevel,
          advertiserId:
            campaignLevel === "INSERTIONORDER"
              ? advertiserId.trim() || undefined
              : undefined,
          campaignFormat: derivedFormat,
          visibleToClients,
          briefingCampaignType:
            selectedCampaignType ||
            (undefined as unknown as components["schemas"]["BriefingCampaignType"]),
        };

        await addAssociatedCampaign({
          id: briefingId,
          body: payload,
        }).unwrap();

        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.associateSuccess",
              "Associated campaign added successfully"
            ),
            severity: "success",
          })
        );
      }

      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.associateSuccess",
            "Associated campaign added successfully"
          ),
          severity: "success",
        })
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg =
        err &&
        typeof err === "object" &&
        "data" in err &&
        (err as { data?: { message?: string } }).data?.message
          ? (err as { data?: { message?: string } }).data!.message!
          : t("briefingsView.associateError", "Failed to add campaign.");
      setError(msg);
      dispatch(
        setToastMessage({
          message: msg,
          severity: "error",
        })
      );
    }
  };

  const currentMapping = platformMappings.find(
    (m) => m.platform === selectedPlatform
  );

  // La lista de plataformas sale del mapping del backend, la misma fuente que usa
  // EditAcInfoDialog. Mantener aqui una copia hardcodeada es lo que hacia que una
  // plataforma nueva (Reddit) apareciese al editar pero no al crear. El prop queda
  // solo como respaldo para el primer render, antes de que lleguen los mappings.
  const platformsFromMappings = platformMappings.map((m) => m.platform);
  const dedupedPlatformOptions = Array.from(
    new Set(
      platformsFromMappings.length > 0 ? platformsFromMappings : platformOptions
    )
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {duplicateMode
          ? t("briefingsView.duplicateAssociatedTitle", "Duplicate campaign")
          : t("briefingsView.addAssociatedCampaign", "Add associated campaign")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
              options={dedupedPlatformOptions}
              value={selectedPlatform || null}
              onChange={(_e, val) =>
                setSelectedPlatform((val as BriefingPlatform | null) ?? "")
              }
              disabled={duplicateMode}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("briefingsView.platform", "Platform")}
                  size="small"
                  autoFocus
                />
              )}
              fullWidth
              size="small"
              sx={{ flexGrow: 1 }}
            />
            {currentMapping && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("platformMappings.mappingDetails", "Mapping Details")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {t("platformMappings.realPlatform", "Real Platform")}:
                      </strong>{" "}
                      {currentMapping.realPlatform || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {t("platformMappings.campaignTypes", "Types")}:
                      </strong>{" "}
                      {currentMapping.campaignTypes.join(", ")}
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <IconButton size="small" color="info" sx={{ flexShrink: 0 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {selectedPlatform && availableCampaignTypes.length > 0 && (
            <Autocomplete
              options={availableCampaignTypes}
              value={selectedCampaignType || null}
              loading={isLoadingMappings}
              onChange={(_, val) =>
                setSelectedCampaignType(
                  (val as components["schemas"]["BriefingCampaignType"]) || ""
                )
              }
              disabled={duplicateMode}
              getOptionLabel={(option) =>
                t(
                  `briefingsCreate.form.briefingCampaignTypeOptions.${option}`,
                  option as string
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("briefingsView.campaignType", "Campaign Type")}
                  size="small"
                />
              )}
              fullWidth
              size="small"
            />
          )}

          <TextField
            select
            fullWidth
            label={t("briefingsView.goalForm.funnel", "Funnel")}
            value={funnel}
            onChange={(e) =>
              setFunnel(
                e.target
                  .value as components["schemas"]["_36_Enums.BriefingFunnel"]
              )
            }
            disabled={duplicateMode}
            size="small"
          >
            {["AWARENESS", "CONSIDERATION", "PERFORMANCE"].map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t("briefingsCreate.form.subcampaignName", "Subcampaign")}
            value={subcampaignName}
            onChange={(e) => {
              userHasEdited.current = true;
              setSubcampaignName(e.target.value);
            }}
            fullWidth
            size="small"
            placeholder={t(
              "briefingsView.subcampaignNamePlaceholder",
              "Optional — leave blank if not applicable"
            )}
          />

          <TextField
            label={t("common.name", "Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />

          {!duplicateMode && (
            <CandidateSelector
              selectedPlatform={selectedPlatform as BriefingPlatform}
              defaultCampaignLevel={defaultCampaignLevel}
              sourceId={sourceId}
              setSourceId={setSourceId}
              setCampaignLevel={setCampaignLevel}
              setAdvertiserId={setAdvertiserId}
              setNativeAdProvider={setNativeAdProvider}
              canFetchCampaigns={canFetchCampaigns}
              isBusy={isLoadingCandidates}
              candidatesError={candidatesError}
              candidatesData={platformCandidatesData?.data}
              handleSave={handleSubmit}
            />
          )}

          {selectedPlatform === "NATADS" && !isAccount && (
            <Autocomplete
              options={["TABOOLA", "OUTBRAIN"]}
              value={nativeAdProvider}
              onChange={(_e, val) =>
                setNativeAdProvider(val as "TABOOLA" | "OUTBRAIN" | null)
              }
              disabled={duplicateMode}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "briefingsView.nativeAdProvider",
                    "Native Ad Provider"
                  )}
                  size="small"
                  required
                />
              )}
              fullWidth
              size="small"
            />
          )}

          {canSetInvestment && (
            <Autocomplete
              options={["CLIENTE", "TI_MEDIA_GROUP_SL"]}
              value={investmentPayment}
              onChange={(_e, val) => {
                paymentEdited.current = true;
                setInvestmentPayment(
                  val as "CLIENTE" | "TI_MEDIA_GROUP_SL" | null
                );
              }}
              disabled={duplicateMode}
              getOptionLabel={(option) =>
                option === "CLIENTE"
                  ? t("briefingsView.paymentClient", "Client")
                  : t(
                      "briefingsView.paymentTiMediaGroupSl",
                      "TI MEDIA GROUP SL"
                    )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "briefingsView.investmentPayment",
                    "Investment Payment"
                  )}
                  size="small"
                  helperText={
                    !duplicateMode &&
                    companyPayment &&
                    investmentPayment === companyPayment
                      ? t(
                          "briefingsView.investmentPaymentFromCompany",
                          "Company default for this platform"
                        )
                      : undefined
                  }
                />
              )}
              fullWidth
              size="small"
            />
          )}

          {/* Remaining fields below (Campaign Type was moved up) */}

          {!isClient && (
            <FormControlLabel
              control={
                <Switch
                  checked={visibleToClients}
                  onChange={(e) => setVisibleToClients(e.target.checked)}
                  disabled={duplicateMode}
                  color="success"
                />
              }
              label={t(
                "briefingsView.visibleToClients",
                "Visible para el cliente"
              )}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            isSaving ||
            !name.trim() ||
            !selectedPlatform ||
            (selectedPlatform === "NATADS" && !isAccount && !nativeAdProvider)
          }
        >
          {duplicateMode
            ? t("briefingsView.duplicateAssociatedConfirm", "Duplicate")
            : t("briefingsView.addAssociatedCampaign", "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAssociatedCampaignDialog;
